const GRID_SELECTOR = '#CollectionLoop';
const CATALOG_FETCH_CONCURRENCY = 2;
const catalogCache = new Map();

const escapeHtml = (value) =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const handleize = (value) =>
  String(value || '')
    .toLowerCase()
    .trim()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

const WEATHER_MATERIALS = {
  hot: ['straw', 'palm', 'bangora', 'raffia', 'toyo', 'shantung', 'panama'],
  mild: ['straw', 'felt', 'palm', 'bangora', 'wool'],
  cold: ['felt', 'wool', 'fur', 'beaver'],
  rain: ['leather', 'felt', 'wool', 'oilskin'],
  year: ['felt', 'wool', 'straw', 'leather'],
};

const USE_SIGNALS = {
  everyday: {
    boost: ['core', 'gift', 'everyday', 'cattleman'],
    penalize: ['rhinestone', 'spark', 'glitzy', 'wedding'],
  },
  ranch: {
    boost: ['ranch', 'cattleman', 'work', 'bangora', 'palm', 'rodeo', 'yellowstone'],
    penalize: ['rhinestone', 'spark', 'glitzy', 'wedding', 'festival'],
  },
  travel: {
    boost: ['raffia', 'packable', 'crushable', 'palm', 'travel', 'lightweight'],
    penalize: ['fur felt', 'beaver'],
  },
  rodeo: {
    boost: ['rodeo', 'cattleman', 'yellowstone', 'western'],
    penalize: ['rhinestone', 'glitzy'],
  },
  special: {
    boost: ['wedding', 'festival', 'rhinestone', 'spark', 'glitzy', 'fur felt', '10x', '20x'],
    penalize: ['bangora', 'work'],
  },
};

const toPresentmentCents = (shopCents, currency) => {
  const converted = Math.round(shopCents * currency.rate);
  if (!currency.usesMarketRounding) return converted;
  return Math.max(0, Math.ceil((converted - 95) / 100) * 100 + 95);
};

const priceRangeFor = (priceKey, currency) => {
  if (priceKey === 'under_100') return [0, toPresentmentCents(10000, currency) - 1];
  if (priceKey === '100_199') return [toPresentmentCents(10000, currency), toPresentmentCents(20000, currency) - 1];
  if (priceKey === '200_299') return [toPresentmentCents(20000, currency), toPresentmentCents(30000, currency) - 1];
  if (priceKey === '300_plus') return [toPresentmentCents(30000, currency), Number.POSITIVE_INFINITY];
  return null;
};

const inPriceRange = (cents, priceKey, currency) => {
  const range = priceRangeFor(priceKey, currency);
  if (!range) return true;
  return cents >= range[0] && cents <= range[1];
};

const priceBandFor = (cents, currency) => {
  if (cents < toPresentmentCents(10000, currency)) return 0;
  if (cents < toPresentmentCents(20000, currency)) return 1;
  if (cents < toPresentmentCents(30000, currency)) return 2;
  return 3;
};

const selectedPriceBandFor = (priceKey) => {
  const bands = {
    under_100: 0,
    '100_199': 1,
    '200_299': 2,
    '300_plus': 3,
  };
  return bands[priceKey] ?? null;
};

const priceDistanceFromRange = (cents, priceKey, currency) => {
  const range = priceRangeFor(priceKey, currency);
  if (!range || inPriceRange(cents, priceKey, currency)) return 0;
  if (cents < range[0]) return range[0] - cents;
  return cents - range[1];
};

const formatMoney = (cents, format = '${{amount}}') => {
  const value = (Number(cents) / 100).toFixed(2);
  return format
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(Number(cents) / 100)))
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, value.replace('.', ','))
    .replace(/\{\{\s*amount\s*\}\}/, value);
};

const formatCurrencyAmount = (cents, currencyCode) => {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: Number(cents) % 100 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(Number(cents) / 100);
  } catch {
    return formatMoney(cents);
  }
};

const detectMaterials = (hay) => {
  const found = [];
  for (const material of [
    'straw',
    'palm',
    'bangora',
    'raffia',
    'toyo',
    'shantung',
    'panama',
    'felt',
    'wool',
    'fur',
    'beaver',
    'leather',
    'suede',
    'denim',
    'cloth',
    'fabric',
    'canvas',
    'cotton',
    'paper',
    'oilskin',
  ]) {
    if (hay.includes(material)) found.push(material);
  }
  return found;
};

const countHits = (hay, words) => words.reduce((total, word) => (hay.includes(word) ? total + 1 : total), 0);

const weatherScore = (materials, weather) => {
  const preferred = WEATHER_MATERIALS[weather] || [];
  const hits = preferred.filter((item) => materials.includes(item)).length;
  if (hits >= 2) return 3;
  if (hits === 1) return 2;
  if (weather === 'year') return 1;
  if (weather === 'hot' && (materials.includes('felt') || materials.includes('wool'))) return 0;
  if (weather === 'cold' && materials.includes('straw') && !materials.includes('felt')) return 0;
  if (weather === 'rain' && materials.includes('straw') && !materials.includes('leather')) return 0;
  return 1;
};

const useScore = (hay, use) => {
  const signals = USE_SIGNALS[use];
  if (!signals) return 1;
  return Math.max(0, 1 + countHits(hay, signals.boost) * 1.5 - countHits(hay, signals.penalize));
};

const priceScore = (cents, priceKey, currency) => {
  const range = priceRangeFor(priceKey, currency);
  if (!range) {
    if (cents >= toPresentmentCents(5000, currency) && cents <= toPresentmentCents(25000, currency)) return 2;
    return 1;
  }
  if (cents >= range[0] && cents <= range[1]) return 3;
  const mid = (range[0] + Math.min(range[1], toPresentmentCents(40000, currency))) / 2;
  const distance = Math.abs(cents - mid);
  if (distance <= toPresentmentCents(5000, currency)) return 1;
  return 0;
};

const comboBonus = (weather, use, materials) => {
  if (weather === 'hot' && use === 'ranch' && (materials.includes('straw') || materials.includes('bangora'))) return 4;
  if (weather === 'cold' && use === 'everyday' && (materials.includes('felt') || materials.includes('wool'))) return 4;
  if (weather === 'rain' && (use === 'ranch' || use === 'travel') && (materials.includes('leather') || materials.includes('felt'))) return 4;
  if (weather === 'mild' && use === 'special' && (materials.includes('felt') || materials.includes('fur'))) return 4;
  return 0;
};

const scoreProduct = (product, answers, currency) => {
  const hay = `${product.title} ${product.tags}`.toLowerCase();
  const metafieldMaterials = detectMaterials(String(product.material || '').toLowerCase());
  const materials = metafieldMaterials.length ? metafieldMaterials : detectMaterials(hay);
  const weather = weatherScore(materials, answers.weather);
  const use = useScore(hay, answers.use);
  const price = priceScore(product.price, answers.price, currency);
  const bonus = comboBonus(answers.weather, answers.use, materials);
  return {
    total: weather * 4 + use * 3 + price * 2 + bonus,
    weather,
    use,
    price,
  };
};

const rankProduct = (product, answers, currency) => {
  const score = scoreProduct(product, answers, currency);
  const selectedBand = selectedPriceBandFor(answers.price);
  const productBand = priceBandFor(product.price, currency);
  const priceTier = selectedBand === null ? 0 : Math.abs(productBand - selectedBand);
  const weatherMatch = score.weather >= 2;
  const useMatch = score.use > 1;
  const exactMatch = weatherMatch && useMatch && priceTier === 0;
  const closestMatch = weatherMatch || useMatch;
  return {
    product,
    score,
    isRelevant: score.weather > 0 && score.use > 0,
    matchTier: exactMatch ? 0 : closestMatch ? 1 : 2,
    priceTier,
    priceDistance: priceDistanceFromRange(product.price, answers.price, currency),
  };
};

const compareRankedProducts = (a, b) => {
  if (a.matchTier !== b.matchTier) return a.matchTier - b.matchTier;
  if (a.matchTier === 1 && a.priceTier !== b.priceTier) return a.priceTier - b.priceTier;
  if (a.matchTier === 1 && a.priceDistance !== b.priceDistance) return a.priceDistance - b.priceDistance;
  if (a.score.total !== b.score.total) return b.score.total - a.score.total;
  if (a.priceTier !== b.priceTier) return a.priceTier - b.priceTier;
  if (a.priceDistance !== b.priceDistance) return a.priceDistance - b.priceDistance;
  return a.product.title.localeCompare(b.product.title);
};

const thumbnailUrl = (src, width = 600) => {
  if (!src) return '';
  try {
    const url = new URL(src, window.location.origin);
    url.searchParams.set('width', String(width));
    return url.href;
  } catch {
    return src;
  }
};

const extractSwatches = (product) => {
  const options = product.options || [];
  const colorOption = options.find((opt) => /colou?r/i.test(opt.name || ''));
  if (!colorOption) return [];

  const optionKey = `option${colorOption.position}`;
  const seen = new Set();
  const swatches = [];

  for (const name of colorOption.values || []) {
    const key = String(name || '').toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);

    const variant = (product.variants || []).find((item) => item[optionKey] === name);
    const cents = variant?.price ? Math.round(parseFloat(variant.price) * 100) : null;
    swatches.push({
      name,
      handle: handleize(name),
      image: thumbnailUrl(variant?.featured_image?.src || ''),
      url: variant?.id ? `/products/${product.handle}?variant=${variant.id}` : `/products/${product.handle}`,
      price: cents,
      swatchValue: `var(--${handleize(name)})`,
    });
  }

  return swatches;
};

const normalizeProduct = (product) => {
  if (!product.variants) {
    const handle = String(product.handle || '').trim();
    return {
      ...product,
      url: product.url || (handle ? `/products/${handle}` : ''),
      tags: Array.isArray(product.tags) ? product.tags.join(' ') : String(product.tags || ''),
      material: String(product.material || ''),
      swatches: product.swatches || [],
    };
  }

  const variants = product.variants || [];
  const availableVariants = variants.filter((variant) => variant.available);
  const pricedVariants = availableVariants.length ? availableVariants : variants;
  const prices = pricedVariants
    .map((variant) => Math.round(parseFloat(variant.price || '0') * 100))
    .filter((price) => price > 0);
  const price = prices.length ? Math.min(...prices) : 0;
  const tags = Array.isArray(product.tags) ? product.tags.join(' ') : String(product.tags || '');
  const image = thumbnailUrl(product.images?.[0]?.src || product.featured_image || '');
  return {
    id: product.id,
    handle: product.handle,
    title: product.title,
    url: `/products/${product.handle}`,
    price,
    available: availableVariants.length > 0,
    tags,
    material: '',
    image,
    swatches: extractSwatches(product),
  };
};

const productIdFromCard = (card) => {
  const id = card.id || '';
  const match = id.match(/(\d+)$/);
  return match ? match[1] : '';
};

class HatFinder extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-hat-finder-dialog]');
    this.scroller = this.querySelector('[data-hat-finder-scroller]');
    this.panels = [...this.querySelectorAll('[data-hat-finder-panel]')];
    this.answers = { weather: '', use: '', price: '' };
    this.ranked = [];
    this.catalog = null;
    this.collectionApplied = false;
    this.step = 1;
    this.pageLocked = false;
    this.savedScrollY = 0;
    const rate = Number(window.Shopify?.currency?.rate);
    this.currency = {
      rate: rate && !Number.isNaN(rate) ? rate : 1,
      usesMarketRounding: this.dataset.activeCurrency !== this.dataset.shopCurrency,
    };
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.updatePriceLabels();

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-hat-finder-open]')) this.open();
      if (event.target.closest('[data-hat-finder-close]')) this.close();
      if (event.target.closest('[data-hat-finder-next]')) this.next();
      if (event.target.closest('[data-hat-finder-prev]')) this.prev();
      if (event.target.closest('[data-hat-finder-see-all]')) this.applyToCollection();

      const swatch = event.target.closest('[data-hat-finder-swatch]');
      if (swatch) {
        event.preventDefault();
        this.selectSwatch(swatch);
        return;
      }

      const option = event.target.closest('[data-hat-finder-option]');
      if (option) this.selectOption(option);
    });
    this.dialog?.addEventListener('close', () => this.onDialogClose());
    this.dialog?.addEventListener('pointerdown', (event) => {
      if (event.target !== this.dialog) return;
      this.close();
    });

    document.addEventListener('infinite-scroll:loaded', () => {
      if (this.collectionApplied) this.reorderGrid();
    });
    document.addEventListener('pagination:loaded', () => {
      if (this.collectionApplied) this.reorderGrid();
    });
  }

  disconnectedCallback() {
    this.unlockPage();
  }

  async open() {
    this.resetQuiz();
    this.updatePriceLabels();
    this.dialog?.showModal();
    this.lockPage();
    this.resetScroller();
    this.querySelector('[data-hat-finder-close]')?.focus();
    this.catalog = this.loadCatalog();
  }

  close() {
    if (this.dialog?.open) this.dialog.close();
  }

  onDialogClose() {
    this.unlockPage();
  }

  lockPage() {
    if (this.pageLocked) return;
    this.savedScrollY = window.scrollY || window.pageYOffset || 0;
    document.documentElement.classList.add('hat-finder-open');
    document.body.style.setProperty('top', `-${this.savedScrollY}px`, 'important');
    this.onTouchMove = (event) => {
      if (event.target.closest('[data-hat-finder-scroller]')) return;
      event.preventDefault();
    };
    document.addEventListener('touchmove', this.onTouchMove, { passive: false });
    this.pageLocked = true;
  }

  unlockPage() {
    if (!this.pageLocked) return;
    document.documentElement.classList.remove('hat-finder-open');
    document.body.style.top = '';
    if (this.onTouchMove) {
      document.removeEventListener('touchmove', this.onTouchMove);
      this.onTouchMove = null;
    }
    window.scrollTo(0, this.savedScrollY);
    this.pageLocked = false;
  }

  resetScroller() {
    if (this.scroller) this.scroller.scrollTop = 0;
  }

  updatePriceLabels() {
    const displayCurrency =
      document.querySelector('.ts-switcher-btn__currency .ts-switcher-btn__name')?.textContent?.trim() ||
      this.dataset.activeCurrency;
    const showsShopCurrency = displayCurrency === this.dataset.shopCurrency;
    const amount = (shopCents) =>
      showsShopCurrency
        ? formatCurrencyAmount(shopCents, displayCurrency)
        : formatMoney(toPresentmentCents(shopCents, this.currency), this.moneyFormat);
    const rangeMaximum = (nextBandCents) =>
      showsShopCurrency
        ? formatCurrencyAmount(nextBandCents - 100, displayCurrency)
        : formatMoney(toPresentmentCents(nextBandCents, this.currency) - 100, this.moneyFormat);
    const labels = {
      under_100: (this.dataset.priceUnderTemplate || 'Under ||amount||').replace('||amount||', amount(10000)),
      '100_199': (this.dataset.price100199Template || '||min||–||max||')
        .replace('||min||', amount(10000))
        .replace('||max||', rangeMaximum(20000)),
      '200_299': (this.dataset.price200299Template || '||min||–||max||')
        .replace('||min||', amount(20000))
        .replace('||max||', rangeMaximum(30000)),
      '300_plus': (this.dataset.price300Template || '||amount||+').replace('||amount||', amount(30000)),
    };

    for (const [priceKey, label] of Object.entries(labels)) {
      const option = this.querySelector(`[data-hat-finder-options="price"] [data-value="${priceKey}"]`);
      const labelElement = option?.querySelector('[data-hat-finder-option-label]');
      if (labelElement) labelElement.textContent = label;
    }
  }

  resetQuiz() {
    this.answers = { weather: '', use: '', price: '' };
    this.collectionApplied = false;
    this.step = 1;
    this.querySelectorAll('[data-hat-finder-option]').forEach((btn) => {
      btn.classList.remove('is-selected');
      btn.setAttribute('aria-checked', 'false');
    });
    this.showStep(1);
  }

  selectOption(btn) {
    const group = btn.closest('[data-hat-finder-options]');
    group?.querySelectorAll('[data-hat-finder-option]').forEach((option) => {
      option.classList.remove('is-selected');
      option.setAttribute('aria-checked', 'false');
    });
    btn.classList.add('is-selected');
    btn.setAttribute('aria-checked', 'true');
    const field = group?.dataset.hatFinderOptions;
    if (field) this.answers[field] = btn.dataset.value || '';
    this.updateNextState();
  }

  currentAnswer() {
    if (this.step === 1) return this.answers.weather;
    if (this.step === 2) return this.answers.use;
    return this.answers.price;
  }

  updateNextState() {
    const hasAnswer = Boolean(this.currentAnswer());
    this.querySelectorAll('[data-hat-finder-next]').forEach((btn) => {
      btn.disabled = !hasAnswer;
    });
  }

  showStep(step) {
    this.step = step;
    this.panels.forEach((panel) => {
      const panelStep = Number(panel.dataset.hatFinderPanel);
      panel.classList.toggle('is-active', panelStep === step);
      panel.querySelectorAll('[data-hat-finder-progress]').forEach((bar, index) => {
        bar.classList.toggle('is-complete', index < step);
      });
    });
    this.updateNextState();
    this.resetScroller();
  }

  async next() {
    if (!this.currentAnswer()) return;
    if (this.step < 3) {
      this.showStep(this.step + 1);
      return;
    }
    await this.showResults();
  }

  prev() {
    if (this.step > 1) this.showStep(this.step - 1);
  }

  async loadCatalog() {
    const handle = this.dataset.collectionHandle;
    const cacheKey = `${handle}:${this.dataset.activeCurrency || ''}`;
    if (catalogCache.has(cacheKey)) return catalogCache.get(cacheKey);

    const catalogPromise = this.fetchCatalog(handle).catch((error) => {
      catalogCache.delete(cacheKey);
      console.warn('[hat-finder] catalog', error);
      return [];
    });
    catalogCache.set(cacheKey, catalogPromise);
    return catalogPromise;
  }

  async fetchCatalogPage(handle, page) {
    const response = await fetch(`/collections/${handle}?view=hat-finder&page=${page}`);
    if (!response.ok) throw new Error(`Catalog page ${page} returned ${response.status}`);
    return response.json();
  }

  async fetchCatalog(handle) {
    const firstPage = await this.fetchCatalogPage(handle, 1);
    const totalPages = Math.max(1, Number(firstPage.pages) || 1);
    const remainingPages = Array.from({ length: totalPages - 1 }, (_, index) => index + 2);
    const pageProducts = [];
    pageProducts[0] = firstPage.products || [];

    const workers = Array.from(
      { length: Math.min(CATALOG_FETCH_CONCURRENCY, remainingPages.length) },
      async () => {
        while (remainingPages.length) {
          const page = remainingPages.shift();
          const json = await this.fetchCatalogPage(handle, page);
          pageProducts[page - 1] = json.products || [];
        }
      }
    );
    await Promise.all(workers);

    return pageProducts.flat().map(normalizeProduct);
  }

  async showResults() {
    const catalog = await this.catalog;
    const ranked = (catalog || [])
      .filter((product) => product.available)
      .map((product) => rankProduct(product, this.answers, this.currency))
      .filter((row) => row.isRelevant)
      .sort(compareRankedProducts);

    this.ranked = ranked;
    this.renderResults(ranked);
    this.showStep(4);
  }

  renderResults(ranked) {
    const featured = ranked[0]?.product;
    const also = ranked.slice(1, 3).map((row) => row.product);
    const featuredRoot = this.querySelector('[data-hat-finder-featured]');
    const alsoRoot = this.querySelector('[data-hat-finder-also]');
    const empty = this.querySelector('[data-hat-finder-empty]');
    const shop = this.querySelector('[data-hat-finder-shop]');
    const alsoTitle = this.querySelector('.hat-finder__also-title');

    if (!featured) {
      if (featuredRoot) featuredRoot.hidden = true;
      if (alsoRoot) alsoRoot.hidden = true;
      if (shop) shop.hidden = true;
      if (alsoTitle) alsoTitle.hidden = true;
      if (empty) empty.hidden = false;
      return;
    }

    if (empty) empty.hidden = true;
    if (featuredRoot) {
      featuredRoot.hidden = false;
      featuredRoot.innerHTML = this.cardMarkup(this.withSwatches(featured), true);
    }
    if (shop) {
      shop.hidden = !featured.url;
      if (featured.url) shop.href = featured.url;
    }
    if (alsoTitle) alsoTitle.hidden = also.length === 0;
    if (alsoRoot) {
      alsoRoot.hidden = also.length === 0;
      alsoRoot.innerHTML = also.map((product) => this.cardMarkup(product, false)).join('');
    }
  }

  cardMarkup(product, featured) {
    const badge = featured
      ? `<span class="hat-finder__badge">${this.dataset.bestMatchLabel || 'Best match'}</span>`
      : '';
    const image = `
      <img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" width="${featured ? 287 : 172}" height="${featured ? 287 : 172}" loading="lazy"${featured ? ' data-hat-finder-card-image' : ''}>
    `;
    const productUrl = product.url || '#';

    if (!featured) {
      return `
        <a class="hat-finder__card" href="${escapeHtml(productUrl)}">
          <div class="hat-finder__card-media">${image}</div>
          <p class="hat-finder__card-title">${escapeHtml(product.title)}</p>
          <p class="hat-finder__card-price">${formatMoney(product.price, this.moneyFormat)}</p>
        </a>
      `;
    }

    return `
      <article class="hat-finder__card hat-finder__card--featured">
        <a class="hat-finder__card-link" href="${escapeHtml(productUrl)}" data-hat-finder-card-link>
          <div class="hat-finder__card-media">
            ${badge}
            ${image}
          </div>
          <p class="hat-finder__card-title">${escapeHtml(product.title)}</p>
        </a>
        ${this.swatchesMarkup(product)}
        <p class="hat-finder__card-price" data-hat-finder-card-price>${formatMoney(product.price, this.moneyFormat)}</p>
      </article>
    `;
  }

  withSwatches(product) {
    const catalog = product.swatches || [];
    const grid = this.gridSwatches(product.id);
    const swatches = grid.length > catalog.length ? grid : catalog;
    return { ...product, swatches };
  }

  gridSwatches(productId) {
    const card = document.querySelector(`${GRID_SELECTOR} [data-grid-item][id$="-${productId}"]`);
    if (!card) return [];

    return [...card.querySelectorAll('[data-sibling-link]')].map((el) => {
      const name = el.getAttribute('data-tooltip') || '';
      const style = el.getAttribute('style') || '';
      const token = style.match(/--swatch:\s*([^;]+)/);
      return {
        name,
        handle: handleize(name),
        image: thumbnailUrl(el.dataset.siblingImage || ''),
        url: el.dataset.siblingLink || '',
        price: null,
        priceText: el.dataset.siblingPrice || '',
        swatchValue: token ? token[1].trim() : `var(--${handleize(name)})`,
      };
    });
  }

  swatchesMarkup(product) {
    const swatches = product.swatches || [];
    if (!swatches.length) return '';
    const currentIndex = swatches.findIndex((item) => (item.url || '').includes(`/products/${product.handle}`));

    return `
      <div class="hat-finder__swatches swatches" role="list">
        ${swatches
          .map((swatch, index) => {
            const selected = currentIndex === index || (currentIndex === -1 && index === 0);
            return `
              <button
                type="button"
                class="hat-finder__swatch swatch-${escapeHtml(swatch.handle)}${selected ? ' is-selected' : ''}"
                role="listitem"
                aria-label="${escapeHtml(swatch.name)}"
                aria-pressed="${selected ? 'true' : 'false'}"
                data-hat-finder-swatch
                data-image="${escapeHtml(swatch.image)}"
                data-url="${escapeHtml(swatch.url)}"
                data-price="${swatch.price ?? ''}"
                data-price-text="${escapeHtml(swatch.priceText || '')}"
                style="--swatch: ${escapeHtml(swatch.swatchValue || `var(--${swatch.handle})`)};"
              ></button>
            `;
          })
          .join('')}
      </div>
    `;
  }

  selectSwatch(swatch) {
    const card = swatch.closest('.hat-finder__card');
    const group = swatch.closest('.hat-finder__swatches');
    group?.querySelectorAll('[data-hat-finder-swatch]').forEach((btn) => {
      const selected = btn === swatch;
      btn.classList.toggle('is-selected', selected);
      btn.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });

    const image = card?.querySelector('[data-hat-finder-card-image]');
    if (image && swatch.dataset.image) image.src = swatch.dataset.image;

    if (swatch.dataset.url) {
      card?.querySelector('[data-hat-finder-card-link]')?.setAttribute('href', swatch.dataset.url);
      const shop = this.querySelector('[data-hat-finder-shop]');
      if (shop) shop.href = swatch.dataset.url;
    }

    if (swatch.dataset.price) {
      const priceEl = card?.querySelector('[data-hat-finder-card-price]');
      if (priceEl) priceEl.innerHTML = formatMoney(swatch.dataset.price, this.moneyFormat);
    } else if (swatch.dataset.priceText) {
      const priceEl = card?.querySelector('[data-hat-finder-card-price]');
      if (priceEl) priceEl.textContent = swatch.dataset.priceText;
    }
  }

  applyToCollection() {
    this.collectionApplied = true;
    this.reorderGrid();
    this.close();
    document.querySelector(GRID_SELECTOR)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  reorderGrid() {
    const grid = document.querySelector(GRID_SELECTOR);
    if (!grid || !this.ranked.length) return;

    const rank = new Map(this.ranked.map((row, index) => [String(row.product.id), index]));
    const cards = [...grid.querySelectorAll('[data-grid-item]')];
    cards
      .map((card, index) => {
        const id = productIdFromCard(card);
        const isRanked = rank.has(id);
        card.hidden = Boolean(id) && !isRanked;
        const rankIndex = isRanked ? rank.get(id) : this.ranked.length + index;
        return { card, rankIndex };
      })
      .sort((a, b) => a.rankIndex - b.rankIndex)
      .forEach((item, index) => {
        item.card.style.order = String(index + 1);
      });
  }
}

if (!customElements.get('hat-finder')) {
  customElements.define('hat-finder', HatFinder);
}
